import { Sparkles, Store, Tag, CalendarCheck, PenLine, Type, Check } from 'lucide-react';
import { PACKAGES, CAMPAIGNS, DURATIONS, SPOT_OPTIONS } from '../lib/pricing';
import { APPT_TIMES, todayPlus } from '../lib/orderValidation';
import { TERMS } from '../data/content';
import SignaturePad from './SignaturePad';

const errCls = 'mt-2 text-sm font-medium text-red-400';

export function Field({ label, required, error, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="label">
        {label} {required && <span className="text-[#FF6B00]">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-[#7D8391]">{hint}</p>}
      {error && <p className={errCls} data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function Choice({ selected, onClick, testid, children, className = '', disabled }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`relative rounded-xl border p-4 text-left transition-[border-color,background-color] duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
        selected ? 'border-[#FF6B00] bg-[#FF6B00]/10' : 'border-[#282C37] bg-[#0F1012] hover:border-[#7D8391]'
      } ${className}`}
    >
      {selected && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6B00]"><Check className="h-3 w-3 text-white" /></span>}
      {children}
    </button>
  );
}

export function StepSelection({ order, update, errors }) {
  const setKind = (kind) => update({ kind, selectionId: null, founder: false });
  return (
    <div data-testid="order-step-1">
      <h3 className="font-display text-2xl font-extrabold tracking-tight">Was möchten Sie buchen?</h3>
      <p className="t-body mt-2">Monatspakete für Partner aus der Region, Kampagnen für Marken und Erzeuger – oder erst einmal eine kostenlose Beratung.</p>
      <div className="mt-7 grid gap-3 sm:grid-cols-3">
        {[
          { id: 'package', label: 'Partner aus der Region', sub: 'Monatspakete ab 390 €', icon: Store },
          { id: 'campaign', label: 'Marke oder Erzeuger', sub: 'Kampagnen ab 1.200 €', icon: Tag },
          { id: 'beratung', label: 'Kostenlose Beratung', sub: '30 Minuten im Markt', icon: CalendarCheck },
        ].map((k) => (
          <Choice key={k.id} testid={`kind-${k.id}`} selected={order.kind === k.id} onClick={() => setKind(k.id)}>
            <k.icon className={`h-5 w-5 ${order.kind === k.id ? 'text-[#FF6B00]' : 'text-[#7D8391]'}`} />
            <p className="mt-3 text-base font-bold">{k.label}</p>
            <p className="mt-0.5 text-xs text-[#7D8391]">{k.sub}</p>
          </Choice>
        ))}
      </div>

      {order.kind === 'package' && (
        <div className="mt-7 space-y-2.5" data-testid="package-options">
          {PACKAGES.map((p) => (
            <Choice key={p.id} testid={`order-package-${p.id}`} selected={order.selectionId === p.id} onClick={() => update({ selectionId: p.id })} className="flex w-full items-center justify-between gap-4 pr-12">
              <span>
                <span className="block font-display text-lg font-bold">{p.name}</span>
                <span className="mt-0.5 block text-sm text-[#B6BCC8]">{p.zone} · {p.contacts}</span>
              </span>
              <span className="shrink-0 font-mono-num text-lg font-bold text-white">{p.price.toLocaleString('de-DE')} €<span className="text-xs font-normal text-[#7D8391]"> /Monat</span></span>
            </Choice>
          ))}
        </div>
      )}

      {order.kind === 'campaign' && (
        <div className="mt-7 space-y-2.5" data-testid="campaign-options">
          {CAMPAIGNS.map((c) => (
            <Choice key={c.id} testid={`order-campaign-${c.id}`} selected={order.selectionId === c.id} onClick={() => update({ selectionId: c.id })} className="flex w-full items-center justify-between gap-4 pr-12">
              <span>
                <span className="block font-display text-lg font-bold">{c.name}</span>
                <span className="mt-0.5 block text-sm text-[#B6BCC8]">{c.duration} · {c.contacts}</span>
              </span>
              <span className="shrink-0 font-mono-num text-lg font-bold text-white">{c.price.toLocaleString('de-DE')} €</span>
            </Choice>
          ))}
        </div>
      )}

      {order.kind === 'beratung' && (
        <div className="mt-7 rounded-xl border border-dashed border-[#FF6B00]/50 bg-[#FF6B00]/5 p-5" data-testid="beratung-info">
          <p className="t-body">Wir zeigen Ihnen die Bildschirme vor Ort, rechnen Ihre Sichtkontakte durch und schlagen ein Paket vor – 30 Minuten im Markt, 0 €, ohne Verpflichtung.</p>
        </div>
      )}
      {errors.selection && <p className={errCls} data-testid="selection-error">{errors.selection}</p>}
    </div>
  );
}

export function StepOptions({ order, update }) {
  const isPremium = order.selectionId === 'premium';
  if (order.kind === 'beratung') {
    return (
      <div data-testid="order-step-2">
        <h3 className="font-display text-2xl font-extrabold tracking-tight">Laufzeit und Extras</h3>
        <p className="t-body mt-4" data-testid="beratung-step2-note">Die Beratung ist kostenfrei – es fallen keine Laufzeiten oder Extras an. Weiter geht es mit Ihrem Wunschtermin.</p>
      </div>
    );
  }
  if (order.kind === 'campaign') {
    return (
      <div data-testid="order-step-2">
        <h3 className="font-display text-2xl font-extrabold tracking-tight">Spotproduktion</h3>
        <p className="t-body mt-2">Kampagnen laufen über die gebuchte Dauer ({CAMPAIGNS.find((c) => c.id === order.selectionId)?.duration}). Laufzeit-Nachlässe gelten für Monatspakete.</p>
        <div className="mt-7"><SpotSelect order={order} update={update} /></div>
      </div>
    );
  }
  return (
    <div data-testid="order-step-2">
      <h3 className="font-display text-2xl font-extrabold tracking-tight">Laufzeit und Extras</h3>
      <p className="t-body mt-2">Je länger die Laufzeit, desto günstiger der Monat. Nach der Mindestlaufzeit können Sie monatlich kündigen.</p>

      <div className="mt-8">
        <p className="label">Laufzeit *</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="duration-options">
          {DURATIONS.map((d) => (
            <Choice key={d.months} testid={`duration-${d.months}`} disabled={order.founder} selected={(order.founder ? 12 : order.months) === d.months} onClick={() => update({ months: d.months })} className="text-center">
              <p className="font-mono-num text-2xl font-bold">{d.months}</p>
              <p className="text-xs text-[#7D8391]">{d.months === 1 ? 'Monat' : 'Monate'}</p>
              <p className={`mt-1.5 text-xs font-semibold ${d.discount > 0 ? 'text-[#FF6B00]' : 'text-[#7D8391]'}`}>{d.note}</p>
            </Choice>
          ))}
        </div>
      </div>

      <Choice
        testid="founder-toggle"
        selected={order.founder}
        onClick={() => update(order.founder ? { founder: false } : { founder: true, months: 12, spotProduction: order.spotProduction === 'none' ? 'standard' : order.spotProduction })}
        className="mt-6 flex w-full items-start gap-3 pr-12"
      >
        <Sparkles className={`mt-0.5 h-5 w-5 shrink-0 ${order.founder ? 'text-[#FF6B00]' : 'text-[#7D8391]'}`} />
        <span>
          <span className="block text-base font-bold">Als Gründungspartner starten</span>
          <span className="mt-1 block text-sm leading-relaxed text-[#B6BCC8]">12 Monate Laufzeit, 20 % Nachlass, Spotproduktion Standard und Branchenexklusivität inklusive. Nur solange einer der zehn Plätze frei ist.</span>
        </span>
      </Choice>

      <div className="mt-8"><SpotSelect order={order} update={update} founder={order.founder} /></div>

      <div className="mt-8">
        <p className="label">Extras</p>
        <div className="space-y-2.5">
          <ExtraCheck testid="extra-exclusivity" checked={order.exclusivity || isPremium || order.founder} disabled={isPremium || order.founder} onChange={(v) => update({ exclusivity: v })} title="Branchenexklusivität" price={isPremium || order.founder ? 'enthalten' : '+ 30 %'} desc="Kein Wettbewerber Ihrer Branche während der Laufzeit" />
          <ExtraCheck testid="extra-timewindow" checked={order.timeWindow || isPremium} disabled={isPremium} onChange={(v) => update({ timeWindow: v })} title="Zeitfenster nach Wunsch" price={isPremium ? 'enthalten' : '+ 15 %'} desc="Nur zu Ihren Zeiten, z. B. Feierabend oder Wochenende" />
          <ExtraCheck testid="extra-sponsoring" checked={order.sponsoring} onChange={(v) => update({ sponsoring: v })} title="Sponsoring Rezept der Woche" price="990 € / Monat" desc="Ihr Name und Logo bei unserem beliebtesten Format" />
        </div>
      </div>
    </div>
  );
}

function SpotSelect({ order, update, founder }) {
  return (
    <div>
      <p className="label">Spotproduktion</p>
      <div className="grid gap-2.5 sm:grid-cols-2" data-testid="select-spot-production">
        {SPOT_OPTIONS.map((s) => {
          const incl = founder && s.id === 'standard';
          return (
            <Choice key={s.id} testid={`spot-option-${s.id}`} selected={order.spotProduction === s.id} onClick={() => update({ spotProduction: s.id })} className="pr-10">
              <span className="flex items-baseline justify-between gap-3">
                <span className="text-base font-bold">{s.name}</span>
                <span className="shrink-0 font-mono-num text-sm font-semibold text-[#FF6B00]">{incl ? 'enthalten' : s.price > 0 ? `${s.price.toLocaleString('de-DE')} €` : '0 €'}</span>
              </span>
              {s.note && <span className="mt-1 block text-xs leading-relaxed text-[#B6BCC8]">{s.note}</span>}
            </Choice>
          );
        })}
      </div>
    </div>
  );
}

export function ExtraCheck({ testid, checked, disabled, onChange, title, price, desc }) {
  return (
    <label
      className={`flex items-start gap-3 rounded-xl border p-4 transition-[border-color,background-color] duration-200 ${
        checked ? 'border-[#FF6B00] bg-[#FF6B00]/10' : 'border-[#282C37] bg-[#0F1012]'
      } ${disabled ? 'opacity-80' : 'cursor-pointer hover:border-[#7D8391]'}`}
      data-testid={`${testid}-label`}
    >
      <input type="checkbox" data-testid={testid} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[#FF6B00]" />
      <span className="flex-1">
        <span className="flex items-center justify-between gap-3">
          <span className="text-base font-bold">{title}</span>
          <span className="shrink-0 font-mono-num text-sm font-semibold text-[#FF6B00]">{price}</span>
        </span>
        <span className="mt-0.5 block text-sm text-[#B6BCC8]">{desc}</span>
      </span>
    </label>
  );
}

export function StepStart({ order, update, errors, minDate }) {
  if (order.kind === 'beratung') {
    return (
      <div data-testid="order-step-3">
        <h3 className="font-display text-2xl font-extrabold tracking-tight">Ihre Wunschtermine</h3>
        <p className="t-body mt-2">Schlagen Sie zwei Termine vor – wir bestätigen einen davon per E-Mail. Die Beratung dauert rund 30 Minuten direkt im Markt.</p>
        <div className="mt-7 grid gap-6 sm:grid-cols-2">
          <Field label="Wunschtermin 1" required error={errors.apptDate1} testid="field-appt1-date">
            <input type="date" data-testid="input-appt1-date" min={todayPlus(2)} value={order.apptDate1} onChange={(e) => update({ apptDate1: e.target.value })} className="input [color-scheme:dark]" />
          </Field>
          <Field label="Tageszeit 1" required error={errors.apptTime1} testid="field-appt1-time">
            <select data-testid="input-appt1-time" value={order.apptTime1} onChange={(e) => update({ apptTime1: e.target.value })} className="input">
              <option value="">Bitte wählen</option>
              {APPT_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Wunschtermin 2 (Alternative)" testid="field-appt2-date">
            <input type="date" data-testid="input-appt2-date" min={todayPlus(2)} value={order.apptDate2} onChange={(e) => update({ apptDate2: e.target.value })} className="input [color-scheme:dark]" />
          </Field>
          <Field label="Tageszeit 2" testid="field-appt2-time">
            <select data-testid="input-appt2-time" value={order.apptTime2} onChange={(e) => update({ apptTime2: e.target.value })} className="input">
              <option value="">Bitte wählen</option>
              {APPT_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Ihre Branche" testid="field-branche" hint="Hilft uns, passende Zonen und Beispiele vorzubereiten">
              <input type="text" data-testid="input-branche" placeholder="z. B. Autohaus, Fitnessstudio, Weingut" value={order.branche} onChange={(e) => update({ branche: e.target.value })} className="input" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Wünsche und Hinweise" testid="field-notes">
              <textarea data-testid="input-notes" rows={4} placeholder="z. B. Themen, die wir vorbereiten sollen" value={order.notes} onChange={(e) => update({ notes: e.target.value })} className="input" />
            </Field>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="order-step-3">
      <h3 className="font-display text-2xl font-extrabold tracking-tight">Start und Spot</h3>
      <p className="t-body mt-2">Der Start ist frühestens zehn Werktage nach Auftragsbestätigung möglich – so bleibt Zeit für Spot und Freigabe.</p>
      <div className="mt-7 grid gap-6 sm:grid-cols-2">
        <Field label="Gewünschter Starttermin" required error={errors.startDate} testid="field-startdate">
          <input type="date" data-testid="input-startdate" min={minDate} value={order.startDate} onChange={(e) => update({ startDate: e.target.value })} className="input [color-scheme:dark]" />
        </Field>
        <Field label="Ihre Branche" required={order.kind !== 'beratung'} error={errors.branche} testid="field-branche" hint="Wichtig für die Branchenexklusivität">
          <input type="text" data-testid="input-branche" placeholder="z. B. Autohaus, Fitnessstudio, Weingut" value={order.branche} onChange={(e) => update({ branche: e.target.value })} className="input" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Wünsche und Hinweise" testid="field-notes">
            <textarea data-testid="input-notes" rows={4} placeholder="z. B. gewünschte Zeitfenster, Aktionszeiträume, Ansprechpartner für den Spot" value={order.notes} onChange={(e) => update({ notes: e.target.value })} className="input" />
          </Field>
        </div>
      </div>
    </div>
  );
}

export function StepContact({ order, update, errors }) {
  const f = (key, label, props = {}) => (
    <Field label={label} required={props.required} error={errors[key]} testid={`field-${props.testid || key}`}>
      <input type={props.type || 'text'} data-testid={`input-${props.testid || key}`} placeholder={props.placeholder} value={order[key]} onChange={(e) => update({ [key]: e.target.value })} className="input" autoComplete={props.auto} />
    </Field>
  );
  return (
    <div data-testid="order-step-4">
      <h3 className="font-display text-2xl font-extrabold tracking-tight">Ihre Daten</h3>
      <p className="t-body mt-2">Pflichtfelder sind mit * markiert. Die Auftragsbestätigung und Rechnung gehen an die angegebene E-Mail-Adresse.</p>
      <div className="mt-7 grid gap-6 sm:grid-cols-2">
        {f('company', 'Unternehmen', { required: true, auto: 'organization' })}
        {f('contactPerson', 'Ansprechpartnerin / Ansprechpartner', { required: true, testid: 'contact', auto: 'name' })}
        {f('street', 'Straße und Hausnummer', { required: true, auto: 'street-address' })}
        <div className="grid grid-cols-3 gap-3">
          {f('zip', 'PLZ', { required: true, auto: 'postal-code' })}
          <div className="col-span-2">{f('city', 'Ort', { required: true, auto: 'address-level2' })}</div>
        </div>
        {f('email', 'E-Mail', { required: true, type: 'email', auto: 'email' })}
        {f('phone', 'Telefon', { type: 'tel', auto: 'tel' })}
        {f('vatId', 'Umsatzsteuer-ID (falls vorhanden)', { placeholder: 'DE…', testid: 'vatid' })}
      </div>
    </div>
  );
}

export function StepSign({ order, update, errors }) {
  return (
    <div data-testid="order-step-5">
      <h3 className="font-display text-2xl font-extrabold tracking-tight">Prüfen und unterschreiben</h3>
      <p className="t-body mt-2">Lesen Sie die Auftragsbedingungen, bestätigen Sie und unterschreiben Sie mit Maus, Finger oder Stift – oder tippen Sie Ihren Namen ein.</p>

      <div className="mt-7">
        <p className="label">Auftragsbedingungen Instore Media (Stand September 2026)</p>
        <div className="max-h-64 space-y-4 overflow-y-auto rounded-xl border border-[#282C37] bg-[#0F1012] p-5" data-testid="terms-box" data-lenis-prevent>
          {TERMS.map((t, i) => (
            <p key={t.slice(0, 40)} className="text-sm leading-relaxed text-[#B6BCC8]" data-testid={`terms-item-${i + 1}`}>
              <span className="font-mono-num font-bold text-[#FF6B00]">{i + 1}. </span>{t}
            </p>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#282C37] bg-[#0F1012] p-4 transition-colors hover:border-[#7D8391]" data-testid="consent-terms-label">
          <input type="checkbox" data-testid="consent-terms" checked={order.consentTerms} onChange={(e) => update({ consentTerms: e.target.checked })} className="mt-1 h-4 w-4 accent-[#FF6B00]" />
          <span className="text-sm text-[#F9FAFB]">Ich habe die Auftragsbedingungen gelesen und akzeptiere sie. *</span>
        </label>
        {errors.consentTerms && <p className={errCls} data-testid="consent-terms-error">{errors.consentTerms}</p>}
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#282C37] bg-[#0F1012] p-4 transition-colors hover:border-[#7D8391]" data-testid="consent-authority-label">
          <input type="checkbox" data-testid="consent-authority" checked={order.consentAuthority} onChange={(e) => update({ consentAuthority: e.target.checked })} className="mt-1 h-4 w-4 accent-[#FF6B00]" />
          <span className="text-sm text-[#F9FAFB]">Ich bin berechtigt, diesen Auftrag für das genannte Unternehmen zu erteilen. *</span>
        </label>
        {errors.consentAuthority && <p className={errCls} data-testid="consent-authority-error">{errors.consentAuthority}</p>}
      </div>

      <div className="mt-8">
        <p className="label">Unterschrift *</p>
        <div className="mb-3 flex gap-2">
          {[
            { id: 'draw', label: 'Zeichnen', icon: PenLine, patch: { signatureMode: 'draw', typedName: '' } },
            { id: 'type', label: 'Tippen', icon: Type, patch: { signatureMode: 'type', signatureData: null } },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`signature-mode-${m.id}`}
              onClick={() => update(m.patch)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-[background-color,color] duration-200 ${
                order.signatureMode === m.id ? 'bg-[#FF6B00] text-white' : 'border border-[#282C37] text-[#B6BCC8] hover:text-white'
              }`}
            >
              <m.icon className="h-3.5 w-3.5" /> {m.label}
            </button>
          ))}
        </div>
        {order.signatureMode === 'draw' ? (
          <SignaturePad onChange={(data) => update({ signatureData: data })} />
        ) : (
          <input type="text" data-testid="input-typed-signature" placeholder="Ihr vollständiger Name" value={order.typedName} onChange={(e) => update({ typedName: e.target.value })} className="input font-display text-xl italic" />
        )}
        {errors.signature && <p className={errCls} data-testid="signature-error">{errors.signature}</p>}
      </div>

      <div className="mt-7 grid gap-6 sm:grid-cols-2">
        <Field label="Ort" testid="field-ort">
          <input type="text" data-testid="input-ort" value={order.ort} onChange={(e) => update({ ort: e.target.value })} className="input" />
        </Field>
        <Field label="Datum" testid="field-datum">
          <input type="date" data-testid="input-datum" value={order.datum} onChange={(e) => update({ datum: e.target.value })} className="input [color-scheme:dark]" />
        </Field>
      </div>
    </div>
  );
}
